import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";

/**
 * Generic CRUD table for library items (Sizes, Papers, Finishes).
 * Props:
 * - title, subtitle, testidPrefix
 * - fetchFn, createFn, updateFn(id, payload), deleteFn(id)
 * - fields: [{ key, label, type: 'text'|'number'|'textarea'|'checkbox', placeholder, colSpan, required, hint, readOnlyOnEdit, step }]
 * - columns: [{ key, label, format?: (v)=>str, align?: 'right'|'left' }]
 * - empty (template object for new item)
 */
const Field = ({ field, value, onChange, isEdit }) => {
  const readOnly = Boolean(field.readOnlyOnEdit && isEdit);
  const base = "input-swiss w-full px-3 py-2 rounded-sm";
  if (field.type === "textarea") {
    return (
      <textarea
        rows={2}
        className={base}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ""}
        data-testid={`field-${field.key}`}
      />
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`field-${field.key}`}
        />
        {field.placeholder || field.label}
      </label>
    );
  }
  return (
    <input
      type={field.type || "text"}
      step={field.step}
      readOnly={readOnly}
      className={base + (readOnly ? " opacity-70 cursor-not-allowed" : "")}
      value={value ?? ""}
      onChange={(e) => onChange(field.type === "number" ? e.target.value : e.target.value)}
      placeholder={field.placeholder || ""}
      data-testid={`field-${field.key}`}
    />
  );
};

const ItemEditor = ({ item, isEdit, fields, onClose, onSave, title }) => {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    for (const f of fields) {
      if (f.required && !String(form[f.key] ?? "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    const clean = {};
    for (const f of fields) {
      let v = form[f.key];
      if (f.type === "number") v = Number(v) || 0;
      if (f.type === "checkbox") v = Boolean(v);
      clean[f.key] = v;
    }
    clean.meta = form.meta || {};
    clean.is_active = form.is_active ?? true;
    setSaving(true);
    try {
      await onSave(clean);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="library-editor-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative ml-auto w-full sm:max-w-md bg-[color:var(--ups-paper)] h-full overflow-y-auto border-l-2 border-[color:var(--ups-ink)]">
        <div className="sticky top-0 z-10 bg-[color:var(--ups-ink)] text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="overline" style={{ color: "#FFB500" }}>
              {isEdit ? "Edit" : "New"} {title}
            </div>
            <h3 className="font-display text-lg font-bold">{form.label || form.id || "Untitled"}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.key} className={f.colSpan === 2 ? "col-span-2" : ""}>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">{f.label}</label>
                <Field
                  field={f}
                  isEdit={isEdit}
                  value={form[f.key]}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                />
                {f.hint && (
                  <p className="text-[11px] font-mono text-[color:var(--ups-subtext)] mt-1">{f.hint}</p>
                )}
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active ?? true}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="field-is_active"
            />
            Active
          </label>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-brutal w-full py-3 px-4 rounded-sm flex items-center justify-center gap-2"
            data-testid="library-save-button"
          >
            {saving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <>Save</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const LibraryTab = ({
  title,
  subtitle,
  testidPrefix,
  fetchFn,
  createFn,
  updateFn,
  deleteFn,
  fields,
  columns,
  empty,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setItems(await fetchFn());
    } catch {
      toast.error(`Failed to load ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async (payload) => {
    const isEdit = Boolean(editing?.__existing);
    if (isEdit) {
      await updateFn(editing.id, payload);
      toast.success(`${title} updated`);
    } else {
      await createFn(payload);
      toast.success(`${title} created`);
    }
    setEditing(null);
    refresh();
  };

  const onDelete = async (id) => {
    if (!window.confirm(`Delete this ${title.toLowerCase().slice(0, -1)}?`)) return;
    try {
      await deleteFn(id);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[color:var(--ups-subtext)]">{subtitle}</p>
        <button
          onClick={() => setEditing({ ...empty })}
          className="btn-brutal px-3 py-2 rounded-sm flex items-center gap-2 text-sm"
          data-testid={`${testidPrefix}-new-button`}
        >
          <Plus size={16} /> New
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[color:var(--ups-subtext)]">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="card-swiss rounded-sm overflow-hidden" data-testid={`${testidPrefix}-table`}>
          <div
            className="grid text-[11px] uppercase font-bold tracking-[0.15em] py-3 px-4 border-b"
            style={{
              gridTemplateColumns: columns.map((c) => c.flex || "1fr").join(" ") + " 90px",
              borderColor: "var(--ups-border)",
              background: "var(--ups-muted)",
            }}
          >
            {columns.map((c) => (
              <div key={c.key} className={c.align === "right" ? "text-right" : ""}>
                {c.label}
              </div>
            ))}
            <div className="text-right">Actions</div>
          </div>
          {items.length === 0 && (
            <div className="p-6 text-sm text-[color:var(--ups-subtext)]">No items yet.</div>
          )}
          {items.map((it) => (
            <div
              key={it.id}
              className="grid text-sm py-3 px-4 border-b hover:bg-[color:var(--ups-muted)] transition items-center"
              style={{
                gridTemplateColumns: columns.map((c) => c.flex || "1fr").join(" ") + " 90px",
                borderColor: "var(--ups-border)",
              }}
              data-testid={`${testidPrefix}-row-${it.id}`}
            >
              {columns.map((c) => (
                <div key={c.key} className={`truncate ${c.align === "right" ? "text-right" : ""} ${c.mono ? "font-mono text-[13px]" : ""}`}>
                  {c.format ? c.format(it[c.key], it) : it[c.key]}
                </div>
              ))}
              <div className="flex justify-end gap-1">
                <button
                  onClick={() => setEditing({ ...it, __existing: true })}
                  className="p-1.5 hover:bg-white rounded-sm"
                  aria-label="Edit"
                  data-testid={`${testidPrefix}-edit-${it.id}`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => onDelete(it.id)}
                  className="p-1.5 hover:bg-red-100 rounded-sm text-red-700"
                  aria-label="Delete"
                  data-testid={`${testidPrefix}-delete-${it.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ItemEditor
          item={editing}
          isEdit={Boolean(editing.__existing)}
          fields={fields}
          title={title.slice(0, -1)}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </>
  );
};

export default LibraryTab;
